import { toFileList } from '../../assistant/utils/datasetSource';

/**
 * Turning whatever the drop zone / file picker hands over into a run queue.
 *
 * `toFileList` is imported rather than re-implemented because the seam it
 * guards has already cost this repo a whole dead feature: on 2026-09-14 the
 * assistant's attach did nothing at all, for every user, because a single
 * `File` was passed to something that called `Array.from` on it — and
 * `Array.from(<File>)` is `[]`, a File having neither `length` nor an iterator.
 * Every util on both sides of that seam was green at the time. See
 * `.claude/memory/` and the CLAUDE.md section on it.
 */

/** Matches `SUPPORTED_IMAGE_EXTENSIONS` in `2_training_api/interface/i_prediction_management.py`. */
export const SUPPORTED_EXTENSIONS = [
  '.bmp', '.dng', '.heic', '.jpeg', '.jpg', '.mpo',
  '.pfm', '.png', '.tif', '.tiff', '.webp',
];

/** What the file `accept` attribute offers — the same set, as one string. */
export const ACCEPT_ATTRIBUTE = SUPPORTED_EXTENSIONS.join(',');

/** Mirrors `MAX_IMAGE_BYTES` on the route, so an oversized file is named here rather than 413'd there. */
export const MAX_IMAGE_BYTES = 40 * 1024 * 1024;

export const extensionOf = (name) => {
  const text = String(name || '');
  const dot = text.lastIndexOf('.');
  return dot < 0 ? '' : text.slice(dot).toLowerCase();
};

/**
 * An object URL for the preview, or `''` where there is no such thing.
 *
 * Guarded rather than assumed: jsdom (which is what `npm test` runs in) does
 * not implement `createObjectURL`, and an unguarded call here would make this
 * module untestable for the sake of one line.
 */
const previewUrlFor = (file) => {
  try {
    if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
      return URL.createObjectURL(file);
    }
  } catch {
    /* fall through to no preview — the queue entry is still valid without one */
  }
  return '';
};

let counter = 0;
const nextId = () => {
  counter += 1;
  return `img-${Date.now().toString(36)}-${counter}`;
};

/**
 * `{ accepted, rejected }` — never a bare array.
 *
 * A file the server would refuse is reported BY NAME with the reason rather
 * than silently dropped. Dropping a folder of 40 photos and being shown 38 with
 * no explanation is the kind of quiet loss that reads as a broken upload.
 */
export const toQueueItems = (input) => {
  const accepted = [];
  const rejected = [];

  toFileList(input).forEach((file) => {
    const name = file?.name || 'image';
    const extension = extensionOf(name);
    if (!SUPPORTED_EXTENSIONS.includes(extension)) {
      rejected.push({ name, reason: 'unsupported' });
      return;
    }
    if (Number(file.size) > MAX_IMAGE_BYTES) {
      rejected.push({ name, reason: 'tooLarge' });
      return;
    }
    accepted.push({
      id: nextId(),
      file,
      name,
      size: Number(file.size) || 0,
      previewUrl: previewUrlFor(file),
      status: 'pending',
      result: null,
      error: '',
    });
  });

  return { accepted, rejected };
};

/** `[pending, running, done, failed]` counts — what the progress strip reports. */
export const queueProgress = (items) => {
  const list = Array.isArray(items) ? items : [];
  const counts = { total: list.length, pending: 0, running: 0, done: 0, failed: 0 };
  list.forEach((item) => {
    if (counts[item.status] !== undefined) counts[item.status] += 1;
  });
  counts.settled = counts.done + counts.failed;
  return counts;
};

/**
 * The next item to run: the first one still pending.
 *
 * Sequential rather than concurrent, deliberately. The forward pass holds a
 * process-wide lock on the server (`_PREDICT_LOCK`) and this API shares one GPU
 * with the training workers under a hard VRAM ceiling, so three lanes would
 * queue behind that lock anyway while making the progress count jump about.
 */
export const nextPending = (items) =>
  (Array.isArray(items) ? items : []).find((item) => item.status === 'pending') || null;
